interface RequestParams {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: { [key: string]: any };
  body?: { [key: string]: any };
  bodyFormat?: 'json' | 'url-encoded' | 'form-data';
  headers?: { [key: string]: any };
}

export class HttpError extends Error {
  name = this.constructor.name;
  json: { [key: string]: any } | null = null;
  declare statusCode: number;
  declare body: string;

  constructor({ statusCode, body }: { statusCode: number; body: string }) {
    super('');

    this.statusCode = statusCode;
    this.body = body;

    try {
      this.json = JSON.parse(body);
    } catch (noop) {
      // just ignore
    }

    this.message = `Request error (${statusCode}): ${body}`;
  }
}

export class JsonParseError extends HttpError {
  declare rawError: Error;

  constructor({
    statusCode,
    body = '',
    error,
  }: {
    statusCode: number;
    body?: string;
    error: Error;
  }) {
    super({ statusCode, body });

    this.message = `Json parse error (${statusCode}): ${error.message}`;
    this.rawError = error;
  }
}

export class NetworkError extends HttpError {
  declare rawError: Error;

  constructor({ error }: { error: Error }) {
    super({ statusCode: 0, body: '' });

    this.message = `Network error: ${error.message}`;
    this.rawError = error;
  }
}

export class NotFoundError extends HttpError {}

export class HttpClient {
  async get<T>(
    url: string,
    params?: Pick<RequestParams, 'query' | 'headers'>,
  ): Promise<T> {
    return await this.request<T>({
      ...params,
      method: 'GET',
      url,
    });
  }

  async post<T>(
    url: string,
    params?: Pick<RequestParams, 'query' | 'headers' | 'body' | 'bodyFormat'>,
  ): Promise<T> {
    return await this.request<T>({
      ...params,
      method: 'POST',
      url,
    });
  }

  async put<T>(
    url: string,
    params?: Pick<RequestParams, 'query' | 'headers' | 'body' | 'bodyFormat'>,
  ): Promise<T> {
    return await this.request<T>({
      ...params,
      method: 'PUT',
      url,
    });
  }

  async patch<T>(
    url: string,
    params?: Pick<RequestParams, 'query' | 'headers' | 'body' | 'bodyFormat'>,
  ): Promise<T> {
    return await this.request<T>({
      ...params,
      method: 'PATCH',
      url,
    });
  }

  async delete<T>(
    url: string,
    params?: Pick<RequestParams, 'query' | 'headers' | 'body' | 'bodyFormat'>,
  ): Promise<T> {
    return await this.request<T>({
      ...params,
      method: 'DELETE',
      url,
    });
  }

  protected async request<T>({
    url,
    method,
    query,
    body,
    bodyFormat = 'json',
    headers,
  }: RequestParams): Promise<T> {
    const queryString = this.formatQueryString(query);

    try {
      let contentType: string | undefined;
      let formattedBody: string | FormData | undefined;

      if (body) {
        switch (bodyFormat) {
          case 'json':
            contentType = 'application/json';
            formattedBody = JSON.stringify(body);
            break;
          case 'url-encoded':
            contentType = 'application/x-www-form-urlencoded';
            formattedBody = this.formatQueryString(body);
            break;
          case 'form-data':
            // don't set contentType here: the browser must compute it itself
            // (multipart/form-data; boundary=...) — setting it manually
            // suppresses the boundary and the server can't parse the body
            // contentType = 'multipart/form-data';
            formattedBody = new FormData();

            for (const [key, value] of Object.entries(body)) {
              if (typeof value === 'undefined') {
                continue;
              }

              formattedBody.append(key, value);
            }

            break;
        }
      }

      const resp = await fetch(`${url}${queryString && `?${queryString}`}`, {
        method,
        body: formattedBody,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(contentType && { 'Content-Type': contentType }),
          ...headers,
        },
      });

      if (resp.status !== 200) {
        const respBody = await resp.text();

        if (resp.status === 404) {
          throw new NotFoundError({
            statusCode: resp.status,
            body: respBody,
          });
        }

        throw new HttpError({
          statusCode: resp.status,
          body: respBody,
        });
      }

      const text = await resp.text();

      if (text === '') {
        // let's count empty body as null because empty string is not a valid JSON
        return null as T;
      }

      try {
        return JSON.parse(text) as T;
      } catch (error) {
        throw new JsonParseError({
          statusCode: resp.status,
          body: text,
          error,
        });
      }
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }

      throw new NetworkError({ error });
    }
  }

  formatQueryString(query?: { [key: string]: any }): string {
    if (!query) {
      return '';
    }

    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(query)) {
      if (typeof value === 'undefined') {
        continue;
      }

      if (Array.isArray(value)) {
        // our backend parses arrays only as repeated key=value params so expand arrays
        for (const item of value) {
          params.append(key, item);
        }

        continue;
      }

      params.set(key, value);
    }

    return params.toString();
  }
}

export const httpClient = new HttpClient();
